import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
	LoButton,
	LoCard,
	LoIcon,
	LoSelect,
	LoSelectOption
} from 'lo-design-system';
import { Subject, takeUntil } from 'rxjs';

/** One slider stop — a single Creator Data API tier/plan, precomputed for display. */
export interface ApiTierView {
	planId: number;
	tierLevel: number;
	/** Active per-month price string, e.g. "945.00" (monthly tier) or "897.75"
	 *  (yearly tier ÷ 12). The `$` sign is prepended by the template. */
	priceValue: string;
	/** Struck regular monthly price shown on a discounted yearly tier, e.g.
	 *  "945.00". The `$` sign is prepended by the template. */
	priceStruck?: string | null;
	/** e.g. "/ month". */
	priceMeta: string;
	/** e.g. "3,000 credits/mo" (Report) or "40,000 requests/mo" (Quick). */
	volumeLabel: string;
	/** e.g. "$0.315 / credit" or "$0.0147 / request". */
	perUnitLabel: string;
	/** CTA text for this tier (Subscribe / Upgrade / Downgrade / Current Plan). */
	actionLabel: string;
	/** Drives the CTA colour variant (plan-card `data-action` values). */
	actionType: string;
	/** Per-tier CTA disable (current / scheduled / unavailable). */
	actionDisabled: boolean;
}

/**
 * A single Creator Data API pricing card in the existing plan-card visual style,
 * but with a volume SELECT that picks one of many tiers instead of rendering one
 * card per tier (it was a slider; `lo` has no slider, and the tiers are discrete
 * stops of a closed list). Presentational: driven by precomputed `tiers`, emits the chosen
 * plan id on the CTA.
 */
@Component({
	standalone: true,
	selector: 'app-creator-data-api-card',
	templateUrl: './creator-data-api-card.component.html',
	styleUrls: ['./creator-data-api-card.component.scss'],
	imports: [ReactiveFormsModule, LoButton, LoCard, LoIcon, LoSelect]
})
export class CreatorDataApiCardComponent implements OnChanges, OnDestroy {
	@Input() title = '';
	@Input() features: string[] = [];
	@Input() tiers: ApiTierView[] = [];
	/** Global disable (e.g. a checkout already in flight) — OR-ed with per-tier. */
	@Input() actionDisabled = false;
	/** Contact ("Talk to us") link — the Enterprise Calendly URL. Opened in place
	 *  of the self-serve CTA while Influencer API is contact-sales only. */
	@Input() contactUrl: string | null = null;
	@Output() select = new EventEmitter<number>();

	readonly indexControl = new FormControl<number>(0, { nonNullable: true });
	maxIndex = 0;
	current: ApiTierView | null = null;
	/** The tiers as the volume select's options, built when `tiers` changes. */
	volumeOptions: LoSelectOption[] = [];

	private destroy$ = new Subject<void>();

	constructor() {
		this.indexControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(index => this.applyIndex(index));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['tiers']) {
			this.volumeOptions = this.tiers.map((tier, index) => ({
				value: String(index),
				label: tier.volumeLabel
			}));
			this.maxIndex = Math.max(0, this.tiers.length - 1);
			const clamped = Math.min(this.indexControl.value, this.maxIndex);
			this.indexControl.setValue(clamped, { emitEvent: false });
			this.applyIndex(clamped);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onAction(): void {
		if (this.current) {
			this.select.emit(this.current.planId);
		}
	}

	onVolume(value: string | string[] | null): void {
		if (typeof value === 'string') {
			this.indexControl.setValue(Number(value));
		}
	}

	onContact(): void {
		if (this.contactUrl) {
			window.open(this.contactUrl, '_blank');
		}
	}

	private applyIndex(index: number): void {
		this.current = this.tiers[index] ?? null;
	}
}
